import { useSyncExternalStore } from 'react';
import { temConversorMoeda, temSeletorIdiomas } from './idiomaMoedaSite';

export { temConversorMoeda, temSeletorIdiomas };

// PREÇO NA MOEDA DO IDIOMA (21/10/2026 — generaliza o conversor do cs2504 de 05/10).
//
// - Gates em utils/idiomaMoedaSite.ts (opções do CRM `seletor_idioma` / `preco_moeda_idioma`).
//   Fora deles os preços seguem com o texto de sempre e NADA aqui roda (nem o pedido de cotação).
// - Moeda = a do PAÍS do navegador para o idioma escolhido: es-AR → AR$, es-ES → €, es-MX → MX$,
//   en-GB → £, fr-CH → CHF… Sem país: en/es → US$, fr/it/de → €. Português é sempre R$.
// - Só o CLIQUE numa opção do seletor do GTranslate decide a moeda (não o `lang` do <html>, que o
//   tradutor alterna sozinho ao reprocessar a página). O `lang` só define o formato dos números.
// - Cotações: /api/cotacoes (hora em hora no servidor); no navegador 1 pedido por hora
//   (localStorage). O SSR sai SEMPRE em real (getServerSnapshot); a moeda salva entra depois da
//   hidratação, sem divergir do HTML do servidor.
// - Cada moeda é validada sozinha: faltou a cotação da origem ou do destino, o preço fica como está.

export type Moeda =
    | 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'CHF'
    | 'ARS' | 'MXN' | 'CLP' | 'COP' | 'UYU' | 'PYG' | 'PEN' | 'BOB'
    | 'BTC' | 'ETH';

/** Símbolo mostrado no preço convertido. Pesos com o prefixo do país, como no cadastro do CRM. */
export const SIMBOLO_MOEDA: Record<Moeda, string> = {
    BRL: 'R$', USD: 'US$', EUR: '€', GBP: '£', CAD: 'CA$', CHF: 'CHF',
    ARS: 'AR$', MXN: 'MX$', CLP: 'CL$', COP: 'CO$', UYU: '$U', PYG: '₲', PEN: 'S/', BOB: 'Bs',
    BTC: '₿', ETH: 'Ξ',
};

/** Select MANUAL do 2504 (topo 3): as 5 moedas que ele pediu. */
export const MOEDAS: { codigo: Moeda; simbolo: string }[] = (['BRL', 'USD', 'EUR', 'BTC', 'ETH'] as Moeda[]).map((codigo) => ({
    codigo,
    simbolo: SIMBOLO_MOEDA[codigo],
}));

// País do navegador → moeda (para o idioma escolhido). "e por aí vai": zona do euro completa.
const REGIAO_MOEDA: Record<string, Moeda> = {
    US: 'USD', GB: 'GBP', CA: 'CAD', CH: 'CHF',
    ES: 'EUR', FR: 'EUR', IT: 'EUR', DE: 'EUR', BE: 'EUR', AT: 'EUR', IE: 'EUR', NL: 'EUR', LU: 'EUR', FI: 'EUR', GR: 'EUR',
    AR: 'ARS', MX: 'MXN', CL: 'CLP', CO: 'COP', UY: 'UYU', PY: 'PYG', PE: 'PEN', BO: 'BOB',
};
// Sem país no navegador: reserva fixa por idioma (decisão do Daniel, 21/10).
const RESERVA_IDIOMA: Record<string, Moeda> = { pt: 'BRL', en: 'USD', es: 'USD', fr: 'EUR', it: 'EUR', de: 'EUR' };

// Moeda de ORIGEM do preço pelo símbolo cadastrado no CRM (`moeda_imovel` / `moeda_tipo`). São os 20
// símbolos do cadastro (R$ US$ € £ AR$ ₲ $U Bs Bs.S CL$ CO$ S/ AED CN¥ JP¥ HK$ S$ ₩ ₹ ฿) mais as
// grafias antigas. Símbolo desconhecido (ou sem cotação) → o preço original.
const MOEDA_DO_SIMBOLO: Record<string, string> = {
    'R$': 'BRL', BRL: 'BRL', 'US$': 'USD', 'U$': 'USD', $: 'USD', USD: 'USD', '€': 'EUR', EUR: 'EUR', '£': 'GBP',
    'AR$': 'ARS', '₲': 'PYG', '$U': 'UYU', Bs: 'BOB', 'Bs.S': 'VES', 'CL$': 'CLP', 'CO$': 'COP', 'S/': 'PEN',
    AED: 'AED', 'CN¥': 'CNY', 'JP¥': 'JPY', 'HK$': 'HKD', 'S$': 'SGD', '₩': 'KRW', '₹': 'INR', '฿': 'THB',
    'MX$': 'MXN', 'CA$': 'CAD', CHF: 'CHF',
};

/** Quantos REAIS vale 1 unidade de cada moeda (`brlPor.USD = 5,03`). Pode faltar moeda. */
type Cotacoes = { atualizadoEm: string; brlPor: Record<string, number> };
type Estado = { moeda: Moeda; cotacoes: Cotacoes | null; idioma: string; locale: string };

const CHAVE_MOEDA = 'moeda_site';
const CHAVE_COTACOES = 'moeda_site_cotacoes_v2';
const VALIDADE_COTACOES_MS = 60 * 60 * 1000;

const ESTADO_SERVIDOR: Estado = { moeda: 'BRL', cotacoes: null, idioma: 'pt', locale: 'pt-BR' };
let estado: Estado = ESTADO_SERVIDOR;
let iniciado = false;
let cotacoesPedidas = false;
const ouvintes = new Set<() => void>();

const atualizar = (parcial: Partial<Estado>) => {
    estado = { ...estado, ...parcial };
    ouvintes.forEach((f) => f());
};

const ler = (chave: string): string | null => {
    try { return window.localStorage.getItem(chave); } catch { return null; }
};
const gravar = (chave: string, valor: string) => {
    try { window.localStorage.setItem(chave, valor); } catch { /* modo privado: só nesta aba */ }
};

const ehMoeda = (v: unknown): v is Moeda => typeof v === 'string' && v in SIMBOLO_MOEDA;
const idiomaDe = (lang: string | null | undefined): string => String(lang || 'pt').slice(0, 2).toLowerCase();
const positivo = (n: unknown): number | null => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null);

/** Tags do navegador ("es-AR", "en-US"…) na ordem de preferência. */
const tagsDoNavegador = (): string[] => {
    if (typeof navigator === 'undefined') return [];
    const lista = Array.isArray(navigator.languages) ? [...navigator.languages] : [];
    if (navigator.language) lista.push(navigator.language);
    return lista.filter(Boolean).map(String);
};

/** 1ª tag do navegador do idioma pedido que tenha PAÍS ("es-419" não é país; "zh-Hant-TW" → TW). */
const regiaoDoNavegador = (idioma: string): string | null => {
    for (const tag of tagsDoNavegador()) {
        const [lingua, ...resto] = tag.split(/[-_]/);
        if (lingua.toLowerCase() !== idioma) continue;
        const pais = resto.find((p) => /^[A-Za-z]{2}$/.test(p));
        if (pais) return pais.toUpperCase();
    }
    return null;
};

/** Moeda do idioma escolhido, pelo país do navegador; sem país, a reserva fixa. Português = real. */
export function moedaDoIdioma(lang: string | null | undefined): Moeda {
    const idioma = idiomaDe(lang);
    if (idioma === 'pt') return 'BRL';
    const regiao = regiaoDoNavegador(idioma);
    return (regiao && REGIAO_MOEDA[regiao]) || RESERVA_IDIOMA[idioma] || 'USD';
}

/** Locale do formato dos números: a tag do navegador do idioma (en-GB, es-AR…) ou o idioma puro. */
const localeDoIdioma = (idioma: string): string => {
    if (idioma === 'pt') return 'pt-BR';
    const regiao = regiaoDoNavegador(idioma);
    return regiao ? `${idioma}-${regiao}` : idioma === 'en' ? 'en-US' : idioma;
};

const cotacoesValidas = (c: any): c is Cotacoes =>
    !!c && typeof c.atualizadoEm === 'string' && !!c.brlPor && typeof c.brlPor === 'object' &&
    Object.values(c.brlPor).some((v) => positivo(v));

async function carregarCotacoes() {
    if (cotacoesPedidas) return;
    cotacoesPedidas = true;
    let guardadas: (Cotacoes & { salvoEm?: number }) | null = null;
    try { guardadas = JSON.parse(ler(CHAVE_COTACOES) || 'null'); } catch { /* ignora */ }
    if (cotacoesValidas(guardadas)) {
        atualizar({ cotacoes: { atualizadoEm: guardadas.atualizadoEm, brlPor: guardadas.brlPor } });
        if (Date.now() - Number(guardadas.salvoEm || 0) < VALIDADE_COTACOES_MS) return;
    }
    try {
        const r = await fetch('/api/cotacoes');
        const c = r.ok ? await r.json() : null;
        if (!cotacoesValidas(c)) return;
        const novas: Cotacoes = { atualizadoEm: c.atualizadoEm, brlPor: c.brlPor };
        gravar(CHAVE_COTACOES, JSON.stringify({ ...novas, salvoEm: Date.now() }));
        atualizar({ cotacoes: novas });
    } catch { /* sem cotação: os preços ficam como estão */ }
}

function observarIdioma() {
    const html = document.documentElement;
    const aplicarIdioma = () => {
        const idioma = idiomaDe(html.getAttribute('lang'));
        if (idioma !== estado.idioma) atualizar({ idioma, locale: localeDoIdioma(idioma) });
    };
    // Formato dos números acompanha o idioma em que a página está.
    new MutationObserver(aplicarIdioma).observe(html, { attributes: true, attributeFilter: ['lang'] });
    // Moeda acompanha a ESCOLHA de idioma: clique numa opção de QUALQUER seletor do GTranslate
    // (o do topo 3 ou o botão flutuante dos demais topos).
    document.addEventListener('click', (e) => {
        const opcao = (e.target as Element | null)?.closest?.('[class*="gtranslate_wrapper"] a[data-gt-lang]');
        if (opcao) definirMoeda(moedaDoIdioma(opcao.getAttribute('data-gt-lang')));
    }, true);
}

function iniciar() {
    if (iniciado || typeof window === 'undefined') return;
    iniciado = true;
    const salva = ler(CHAVE_MOEDA);
    const idioma = idiomaDe(document.documentElement.getAttribute('lang'));
    atualizar({ moeda: ehMoeda(salva) ? salva : 'BRL', idioma, locale: localeDoIdioma(idioma) });
    observarIdioma();
}

/**
 * Chamado pelo seletor de idiomas ao montar (SeletorIdiomas): garante o ouvinte do clique mesmo numa
 * página SEM preço (home institucional, /empresa…) — senão a escolha de English feita ali não viraria
 * dólar na página seguinte. Com o conversor, já pede a cotação (hora em hora) para a próxima página.
 */
export const prepararMoedaDoIdioma = (converte: boolean) => {
    iniciar();
    if (converte) void carregarCotacoes();
};

// Duas assinaturas: com conversor, pede a cotação; só com o seletor (preço em `notranslate`, sem
// converter), não gasta um pedido. Sem gate nenhum, o hook nem assina (ver useEstadoMoeda).
const assinarComCotacoes = (f: () => void) => {
    ouvintes.add(f);
    iniciar();
    void carregarCotacoes();
    return () => { ouvintes.delete(f); };
};
const assinarSemCotacoes = (f: () => void) => {
    ouvintes.add(f);
    iniciar();
    return () => { ouvintes.delete(f); };
};
const assinarNada = () => () => {};
const lerEstado = () => estado;
const lerEstadoServidor = () => ESTADO_SERVIDOR;

/**
 * Estado da moeda. `ativo=false` (tenant fora do gate) não assina nada — nenhum efeito colateral
 * nos ~300 sites. `converte=false` (só o seletor ligado) não pede cotação.
 */
export const useEstadoMoeda = (ativo = true, converte = true): Estado =>
    useSyncExternalStore(
        !ativo ? assinarNada : converte ? assinarComCotacoes : assinarSemCotacoes,
        ativo ? lerEstado : lerEstadoServidor,
        lerEstadoServidor
    );

export const definirMoeda = (moeda: Moeda) => {
    gravar(CHAVE_MOEDA, moeda);
    atualizar({ moeda });
};

/** "21/10 13:08" (hora de São Paulo) da cotação em uso, para a dica do preço convertido. */
export const dataDaCotacao = (est: Estado): string => {
    const iso = est.cotacoes?.atualizadoEm;
    const d = iso ? new Date(iso) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(d).replace(',', '');
};

/**
 * Texto do preço no tenant com conversor. `original` é o texto que o ponto de chamada já mostrava
 * (cada card tem o seu: "R$ 1.250.000" no card 3, "R$ 1.250.000,00" nos outros) e volta INTACTO
 * em real, sem cotação, com moeda de origem desconhecida ou igual à de destino.
 * Convertido: "≈ US$ 401.234", "≈ AR$ 150.370.000", "≈ 4,6594 BTC" (aproximado: cotação da hora).
 */
export function formatarPreco(valor: number, simboloOrigem: string | null | undefined, original: string, est: Estado): string {
    const origem = MOEDA_DO_SIMBOLO[String(simboloOrigem ?? '').trim()];
    const brlPor = est.cotacoes?.brlPor;
    const n = Number(valor);
    if (est.moeda === 'BRL' || !brlPor || !origem || origem === est.moeda || !(n > 0)) return original;

    const reaisPorOrigem = origem === 'BRL' ? 1 : positivo(brlPor[origem]);
    const reaisPorDestino = positivo(brlPor[est.moeda]);
    if (!reaisPorOrigem || !reaisPorDestino) return original;

    const destino = (n * reaisPorOrigem) / reaisPorDestino;
    const locale = est.locale || 'pt-BR';
    if (est.moeda === 'BTC' || est.moeda === 'ETH') {
        const casas = est.moeda === 'BTC' ? 4 : 3;
        const num = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: casas }).format(destino);
        return `≈ ${num} ${est.moeda}`;
    }
    const num = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(destino);
    return `≈ ${SIMBOLO_MOEDA[est.moeda]} ${num}`;
}

export type FormatadorPreco = (valor: number, simbolo: string | null | undefined, original: string) => string;

/**
 * Para cards cujo preço sai de um helper de string (precoDoImovel, precosRotulados): devolve o
 * formatador a passar ao helper — ou `undefined` fora do gate, e aí o helper devolve o texto de
 * sempre (byte a byte). Com só o seletor ligado, devolve o próprio texto (o card ainda embrulha em
 * `notranslate`); com o conversor, converte.
 */
export function usePrecoMoeda(site: any): FormatadorPreco | undefined {
    const ativo = temSeletorIdiomas(site);
    const converte = temConversorMoeda(site);
    const est = useEstadoMoeda(ativo, converte);
    if (!ativo) return undefined;
    if (!converte) return (_valor, _simbolo, original) => original;
    return (valor, simbolo, original) => formatarPreco(valor, simbolo, original, est);
}
